/**
 * Página do Serviço: Ponto Facial
 * WorkStream - Controle de ponto por reconhecimento facial
 */
import { Link } from 'react-router-dom'
import { 
  ScanFace, Clock, MapPin, Shield, Smartphone,
  ArrowRight, Play, AlertTriangle
} from 'lucide-react'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'

const features = [
  {
    icon: ScanFace,
    title: 'Reconhecimento Facial',
    description: 'Identificação em menos de 1 segundo com 98.7% de precisão usando ArcFace Deep Learning.',
  },
  {
    icon: Shield,
    title: 'Anti-Fraude',
    description: 'Detecção de fotos, vídeos e máscaras. Impossível fraudar o sistema.',
  },
  {
    icon: MapPin,
    title: 'Geolocalização',
    description: 'Validação de perímetro para garantir que o ponto seja registrado no local correto.',
  },
  {
    icon: Smartphone,
    title: 'Multi-dispositivo',
    description: 'Totem fixo, app mobile, câmera IP ou quiosque. Escolha o melhor para sua operação.',
  },
  {
    icon: Clock,
    title: 'Banco de Horas',
    description: 'Cálculo automático de horas extras, atrasos e faltas com relatórios detalhados.',
  },
  {
    icon: AlertTriangle,
    title: 'Alertas em Tempo Real',
    description: 'Notificações instantâneas de atrasos, faltas e anomalias para gestores.',
  },
]

const stats = [
  { value: '98.7%', label: 'Precisão' },
  { value: '<1s', label: 'Tempo de ID' },
  { value: '24/7', label: 'Monitoramento' },
  { value: '0%', label: 'Fraude' },
]

const howItWorks = [
  {
    step: '01',
    title: 'Cadastro Facial',
    description: 'Colaborador registra seu rosto uma única vez. O sistema cria um embedding facial único e seguro.',
  },
  {
    step: '02',
    title: 'Registro de Ponto',
    description: 'Colaborador olha para a câmera. Em menos de 1 segundo o sistema identifica e registra.',
  },
  {
    step: '03',
    title: 'Validação Automática',
    description: 'O sistema valida localização, horário e anti-fraude automaticamente.',
  },
  {
    step: '04',
    title: 'Gestão Inteligente',
    description: 'Gestores recebem alertas e relatórios. Aprovações e ajustes pelo painel.',
  },
]

export default function PontoFacial() {
  return (
    <div className="min-h-screen bg-[#0B1120]">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-30" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-gradient-to-br from-cyan-500/20 to-transparent rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm font-medium mb-6">
              <ScanFace className="w-4 h-4" />
              SERVIÇO 1 - PONTO FACIAL
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Ponto facial.{' '}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Controle total.
              </span>
            </h1>
            
            <p className="text-xl text-slate-400 mb-8 max-w-2xl mx-auto">
              Elimine fraudes e burocracia. Reconhecimento facial com 98.7% de precisão, 
              anti-spoofing avançado e integração com folha de pagamento.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/demo"
                className="px-8 py-4 rounded-xl font-semibold text-white transition-all flex items-center gap-2"
                style={{ 
                  background: 'linear-gradient(135deg, #00C6FF 0%, #0072FF 100%)',
                  boxShadow: '0 4px 14px 0 rgba(0, 198, 255, 0.39)'
                }}
              >
                Testar Grátis
                <ArrowRight className="w-5 h-5" />
              </Link>
              <button className="px-8 py-4 rounded-xl font-semibold text-white bg-slate-800 border border-slate-700 hover:bg-slate-700 transition-all flex items-center gap-2">
                <Play className="w-5 h-5" />
                Ver Demo
              </button>
            </div>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16 max-w-3xl mx-auto">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center p-4 rounded-xl bg-slate-800/50 border border-slate-700">
                <p className="text-3xl font-bold text-cyan-400">{stat.value}</p>
                <p className="text-sm text-slate-400">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Features */}
      <section className="py-20 bg-[#0F172A]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Recursos do Ponto Facial
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Tecnologia de ponta para controle de ponto sem complicações
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => {
              const Icon = feature.icon
              return (
                <div
                  key={feature.title}
                  className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700 hover:border-cyan-500/30 transition-all group"
                >
                  <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="w-6 h-6 text-cyan-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-slate-400">{feature.description}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>
      
      {/* How it Works */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Como Funciona
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Simples de implementar, fácil de usar
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((item, index) => (
              <div key={item.step} className="relative">
                {index < howItWorks.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-cyan-500/50 to-transparent" />
                )}
                <div className="text-5xl font-bold text-cyan-500/20 mb-4">{item.step}</div>
                <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-slate-400">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-cyan-500/10 to-blue-600/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Pronto para eliminar fraudes no ponto?
          </h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">
            Comece gratuitamente por 14 dias. Sem cartão de crédito.
          </p>
          <Link
            to="/demo"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-white transition-all"
            style={{ 
              background: 'linear-gradient(135deg, #00C6FF 0%, #0072FF 100%)',
              boxShadow: '0 4px 14px 0 rgba(0, 198, 255, 0.39)'
            }}
          >
            Começar Teste Grátis
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
      
      <Footer />
    </div>
  )
}

